import logging from 'utils/logging';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
} from 'features/gamestate/constants';
import { HOST_STATUS, MOMENT_TYPES, NARRATION_KEYS } from '../hostStateSlice';
import { advanceState } from '../../gamestate/GameState';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.DailyDoubleWagerReaction');
log.setLevel('info');

const ddWagerReacted = new Set();
const ddWagerPlayed = new Set();

export const resetDailyDoubleWagerCache = () => {
  ddWagerReacted.clear();
  ddWagerPlayed.clear();
};
registerCacheReset('round', resetDailyDoubleWagerCache);

export const DailyDoubleWagerReactionMoment = {
  id: 'DAILY_DOUBLE_WAGER_REACTION',
  shouldTrigger: ({ currentState, currentPlayers, submissions }) => {
    if (currentState?.currentState !== GAMESTATE_ROUND) {
      return false;
    }

    if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_WAGERING) {
      return false;
    }

    // Only trigger for Daily Double questions
    if (!currentState?.dailyDouble) {
      return false;
    }

    const questionId = currentState?.questionId;
    if (!questionId) {
      return false;
    }

    if (ddWagerReacted.has(questionId)) {
      return false;
    }

    const playerId = currentState?.playerInControl;
    const player = currentPlayers?.find(p => p.id === playerId);
    if (!player) {
      return false;
    }

    // Find the controlling player's submission with a wager
    const playerSubmission = submissions?.find(s => s.id === playerId);
    const wagerAmount = playerSubmission?.submission?.wager;
    if (wagerAmount === undefined || wagerAmount === null) {
      log.debug('No wager found in submissions for DD wager reaction');
      return false;
    }

    ddWagerReacted.add(questionId);

    log.info(`Daily Double wager reaction triggered: ${player.name} wagered $${wagerAmount}`);

    const playerScore = player.score || 0;
    const isTrueDailyDouble = wagerAmount >= playerScore && playerScore > 0;
    const isMinimumWager = wagerAmount <= 5;

    // Compute standings context
    const sortedPlayers = [...(currentPlayers || [])].sort((a, b) => (b.score || 0) - (a.score || 0));
    const leader = sortedPlayers[0];
    const leaderScore = leader?.score || 0;
    const projectedScore = playerScore + wagerAmount;
    const wouldTakeLead = !leader || leader.id === player.id ? false : projectedScore > leaderScore;
    const secondPlace = sortedPlayers.find(p => p.id !== player.id);
    const secondPlaceScore = secondPlace?.score || 0;
    const wouldLoseLead = leader?.id === player.id && (playerScore - wagerAmount) < secondPlaceScore;

    const narrationKey = `${NARRATION_KEYS.DAILY_DOUBLE_WAGER}_${questionId}`;

    return {
      shouldTriggerAction: false,
      shouldGenerateNarration: true,
      narrationParams: {
        narrationKey,
        momentType: MOMENT_TYPES.DAILY_DOUBLE_WAGER,
        context: {
          questionId,
          category: currentState?.category,
          playerName: player.name,
          playerScore,
          wagerAmount,
          isTrueDailyDouble,
          isMinimumWager,
          wouldTakeLead,
          wouldLoseLead,
          projectedScore,
          leaderScore,
        }
      }
    };
  }
};

export const DailyDoubleWagerReactionPlaybackMoment = {
  id: 'DAILY_DOUBLE_WAGER_REACTION_PLAYBACK',
  momentType: MOMENT_TYPES.DAILY_DOUBLE_WAGER,
  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration) {
      return false;
    }

    if (narration.momentType !== MOMENT_TYPES.DAILY_DOUBLE_WAGER) {
      return false;
    }

    // Play during question.wagering when it's a Daily Double
    if (gameState?.currentState !== GAMESTATE_ROUND || gameState?.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_WAGERING) {
      return false;
    }

    if (!gameState?.dailyDouble) {
      return false;
    }

    const narrationKey = narration?.key;
    if (ddWagerPlayed.has(narrationKey)) {
      return false;
    }

    if (gameState?.questionId !== narration?.context?.questionId) {
      return false;
    }

    if (hostState?.status === HOST_STATUS.SPEAKING) {
      return false;
    }

    const narrationReady = narration.textStatus === "READY" && (narration.audioStatus === "READY" || narration.audioStatus === "ERROR");
    if (!narrationReady) {
      return false;
    }

    ddWagerPlayed.add(narrationKey);
    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Wager reaction narration complete, advancing to question.reading');
    try {
      const state = getState();
      const gameObject = state.game?.game;
      if (!gameObject) {
        log.error('Game object not found in state');
        return;
      }
      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing after wager reaction:', error);
    }
  }
};

const api = {
  trigger: DailyDoubleWagerReactionMoment,
  playback: DailyDoubleWagerReactionPlaybackMoment
};

export default api;
